import React from 'react'
import { useFormContext } from 'react-hook-form'
import styled from '@emotion/styled'
import InputField from 'modules/ui/molecules/FormComponents/InputField'
import Column from 'modules/ui/atoms/Layout/Column'
import Row from 'modules/ui/atoms/Layout/Row'
import { useTranslation } from 'next-i18next'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'

// Form data type for donor info fields
export interface DonorInfoFormData {
  email: string
  firstName: string
  lastName: string
  country: string
  zipCode: string
}

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`

// Skeleton component for loading state
const DonorInfoFieldsSkeleton = () => (
  <FieldsContainer>
    <Row>
      <Column>
        <TextPlaceloader w={85} fs="14px" lh="20px" />
        <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
      </Column>
    </Row>

    <Row>
      <Column>
        <TextPlaceloader w={68} fs="14px" lh="20px" />
        <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
      </Column>
      <Column>
        <TextPlaceloader w={68} fs="14px" lh="20px" />
        <Placeloader style={{ width: '100%', height: 45, borderRadius: 8 }} />
      </Column>
    </Row>
  </FieldsContainer>
)

type DonorInfoFieldsProps = {
  isLoading?: boolean
}

const DonorInfoFields = ({ isLoading = false }: DonorInfoFieldsProps) => {
  const { t } = useTranslation()
  const form = useFormContext<DonorInfoFormData>()

  const {
    formState: { errors },
  } = form

  const fieldError = (fieldName: keyof DonorInfoFormData) =>
    errors[fieldName] as { message: string } | undefined

  if (isLoading) {
    return <DonorInfoFieldsSkeleton />
  }

  return (
    <FieldsContainer>
      {/* Email - Full width */}
      <Row>
        <Column>
          <InputField
            required
            id="email"
            type="email"
            label={t('DonorInfoFields.email.label', 'Email')}
            error={fieldError('email')}
            autoComplete="email"
            dataId="donor-info-email-input"
          />
        </Column>
      </Row>

      {/* First Name and Last Name - Side by side */}
      <Row>
        <Column>
          <InputField
            required
            id="firstName"
            label={t('DonorInfoFields.firstName.label', 'First name')}
            error={fieldError('firstName')}
            autoComplete="given-name"
            dataId="donor-info-first-name-input"
          />
        </Column>
        <Column>
          <InputField
            required
            id="lastName"
            label={t('DonorInfoFields.lastName.label', 'Last name')}
            error={fieldError('lastName')}
            autoComplete="family-name"
            dataId="donor-info-last-name-input"
          />
        </Column>
      </Row>
    </FieldsContainer>
  )
}

export default DonorInfoFields
